"use client";

import React, { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type TimelineStep = {
  number: string;
  title: string;
  description: string;
  image: string;
};
const steps: TimelineStep[] = [{
  number: "01",
  title: "Consulto Iniziale",
  description: "Invii una panoramica o alcune foto e ricevi un piano scritto entro 24 ore, con le voci di prezzo e i giorni di permanenza, prima ancora di partire.",
  image: "https://impiantidentali.co/wp-content/uploads/2026/05/WhatsApp-Image-2026-05-17-at-6.29.22-PM.jpeg?v=598j65n0ez"
}, {
  number: "02",
  title: "Il Viaggio e l'Intervento",
  description: "Accoglienza in aeroporto e soggiorno in hotel convenzionati. Gli impianti vengono inseriti presso SkyDental a Tirana.",
  image: "https://impiantidentali.co/wp-content/uploads/2026/05/640-0-1695208122xrinasss-554.jpg?v=w85jqdo6m5g"
}, {
  number: "03",
  title: "Il Tuo Nuovo Sorriso",
  description: "Finalizzazione protesica e controlli post-operatori, secondo il calendario scritto nel piano.",
  image: "https://impiantidentali.co/wp-content/uploads/2026/05/WhatsApp-Image-2026-05-17-at-6.36.43-PM.jpeg?v=gwsrdivuti"
}];
export default function FromPrepToPublish() {
  const sectionRef = useRef<HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const totalScrollableDistance = rect.height;
      const scrolledDistance = windowHeight / 2 - rect.top;
      let currentProgress = scrolledDistance / totalScrollableDistance * 100;
      currentProgress = Math.max(0, Math.min(100, currentProgress));
      setProgress(currentProgress);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="224600" id="il-percorso-del-paziente-in-3-fasi" ref={sectionRef} className="bg-background py-24 md:py-36 overflow-hidden relative">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-primary/5 rounded-2xl blur-[140px] pointer-events-none" />

      <div className="container mx-auto px-6 max-w-7xl relative z-10">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-20 md:mb-28 transition-all duration-1000 ${isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground mb-6 tracking-tight">
            Il Percorso del Paziente in 3 Fasi
          </h2>
          <p className="text-lg text-muted-foreground font-sans">
            Dalla prima consulenza online al controllo finale: un percorso chiaro, con ogni passaggio scritto nel piano.
          </p>
        </div>

        <div ref={containerRef} className="relative">
          <div className="absolute left-[2.5rem] top-0 h-[calc(100%-6rem)] w-1 md:left-0 md:top-[2.5rem] md:h-1 md:w-full bg-border/70 rounded-2xl z-0">
            <div className="absolute left-0 top-0 bg-primary transition-all duration-150 ease-out rounded-2xl md:h-full md:w-[var(--progress)] h-[var(--progress)] w-full" style={{
            "--progress": `${progress}%`
          } as React.CSSProperties} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-16 md:gap-10 lg:gap-12 relative z-10">
            {steps.map((step, i) => <TimelineNode key={i} step={step} index={i} progress={progress} />)}
          </div>

          <div className="mt-20 text-center">
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-secondary/80 border border-border text-sm font-mono text-primary shadow-xs">
              <span className="w-2 h-2 rounded-lg bg-chart-2 animate-pulse" />
              Giorni e viaggi indicati nel piano scritto
            </div>
          </div>

        </div>
      </div>
    </section>;
}
function TimelineNode({
  step,
  index,
  progress
}: {
  step: TimelineStep;
  index: number;
  progress: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const nodeThreshold = index * 33;
  const isActive = progress > nodeThreshold;
  return <div ref={ref} data-index={index} className={`flex flex-col items-start md:items-center relative transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'}`} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <div className={`size-20 rounded-2xl bg-card border-2 flex items-center justify-center text-2xl font-mono font-bold mb-8 transition-all duration-500 z-10
          ${isActive ? 'border-primary text-primary shadow-sm bg-accent/30 scale-105' : 'border-border text-muted-foreground scale-100 shadow-2xs'}
        `}>
        {step.number}
      </div>

      <div className="w-full pl-24 md:pl-0">
        <div className={`rounded-2xl overflow-hidden border bg-card p-5 sm:p-6 transition-all duration-700 delay-200 hover:-translate-y-1 ${isVisible ? 'opacity-100 scale-100 shadow-sm hover:shadow-md border-border' : 'opacity-0 scale-95 border-transparent'}`}>
          <div className="relative rounded-xl overflow-hidden bg-muted aspect-[4/3] mb-6 border border-border/50">
            <img src={step.image} alt={step.title} className={`w-full h-full object-cover transition-transform duration-700 ${index === 1 ? 'hover:scale-95' : 'hover:scale-105'}`} loading="lazy" data-wvc-srcset={`${step.image}-wvc-srcset`} data-wvc-sizes={`${step.image}-wvc-sizes`} />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/15 via-transparent to-transparent pointer-events-none" />
          </div>

          <div className="space-y-3 text-left md:text-center">
            <h3 className={`text-xl md:text-2xl font-serif font-bold transition-colors duration-300 ${isActive ? 'text-foreground' : 'text-foreground/80'}`}>
              {step.title}
            </h3>
            <p className="text-muted-foreground font-sans leading-relaxed text-sm md:text-base">
              {step.description}
            </p>
          </div>
        </div>
      </div>
    </div>;
}